import React, { useState, useRef, useEffect } from 'react';
import { Send, History, Star, Download, Trash2, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/hooks/use-toast';
import { useLLM } from '@/hooks/useLLM';
import { QueryUnderstanding } from './QueryUnderstanding';
import { ResponseGeneration } from './ResponseGeneration';
import { VoiceInterface } from './VoiceInterface';

export interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  queryType?: 'performance' | 'planning' | 'comparative' | 'predictive' | 'general';
  confidence?: number;
  visualizations?: any[];
  actions?: string[];
  favorite?: boolean;
}

export interface ConversationSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
  tags: string[];
  playerId?: string;
  sessionId?: string;
}

interface ConversationalInterfaceProps {
  playerId?: string;
  sessionId?: string;
  className?: string;
}

export function ConversationalInterface({ 
  playerId, 
  sessionId, 
  className 
}: ConversationalInterfaceProps) {
  // State management
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState<ConversationSession[]>([]);
  const [currentConversation, setCurrentConversation] = useState<ConversationSession | null>(null);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // LLM service
  const { isConnected, analyzeShot: _analyzeShot, service: _service } = useLLM();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load conversation history on mount
  useEffect(() => {
    loadConversationHistory();
  }, []);

  const loadConversationHistory = async () => {
    try {
      // In a real app, this would load from backend
      const savedConversations = localStorage.getItem('cricket-conversations');
      if (savedConversations) {
        const parsed = JSON.parse(savedConversations);
        setConversations(parsed.map((conv: any) => ({
          ...conv,
          createdAt: new Date(conv.createdAt),
          updatedAt: new Date(conv.updatedAt),
          messages: conv.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        })));
      }
    } catch (error) {
      console.error('Failed to load conversation history:', error);
    }
  };

  const saveConversation = async (conversation: ConversationSession) => {
    try {
      const updated = conversations.map(conv => 
        conv.id === conversation.id ? conversation : conv
      );
      if (!updated.find(conv => conv.id === conversation.id)) {
        updated.push(conversation);
      }
      setConversations(updated);
      localStorage.setItem('cricket-conversations', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save conversation:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: `msg_${Date.now()}_user`,
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Process query through natural language understanding
      const response = await processNaturalLanguageQuery(userMessage.content);
      
      const assistantMessage: ChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        type: 'assistant',
        content: response.content,
        timestamp: new Date(),
        queryType: response.queryType,
        confidence: response.confidence,
        visualizations: response.visualizations,
        actions: response.actions
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Update current conversation
      if (currentConversation) {
        const updated = {
          ...currentConversation,
          messages: [...currentConversation.messages, userMessage, assistantMessage],
          updatedAt: new Date()
        };
        setCurrentConversation(updated);
        await saveConversation(updated);
      } else {
        // Create new conversation
        const newConversation: ConversationSession = {
          id: `conv_${Date.now()}`,
          title: generateConversationTitle(userMessage.content),
          messages: [userMessage, assistantMessage],
          createdAt: new Date(),
          updatedAt: new Date(),
          tags: extractTags(userMessage.content),
          playerId,
          sessionId
        };
        setCurrentConversation(newConversation);
        await saveConversation(newConversation);
      }
      
    } catch (error) {
      console.error('Failed to process message:', error);
      const errorMessage: ChatMessage = {
        id: `msg_${Date.now()}_error`,
        type: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
        confidence: 0
      };
      setMessages(prev => [...prev, errorMessage]);
      
      toast({
        title: 'Analysis Error',
        description: 'Failed to process your query. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const processNaturalLanguageQuery = async (query: string) => {
    // This would integrate with the LLM service for natural language processing
    const response = await fetch('/api/analytics/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query,
        context: {
          playerId,
          sessionId,
          recentMessages: messages.slice(-5)
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to process query');
    }

    return response.json();
  };

  const generateConversationTitle = (firstMessage: string): string => {
    const words = firstMessage.split(' ').slice(0, 5).join(' ');
    return words.length > 30 ? words.substring(0, 30) + '...' : words;
  };

  const extractTags = (message: string): string[] => {
    const tags: string[] = [];
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('performance') || lowerMessage.includes('stats')) tags.push('performance');
    if (lowerMessage.includes('compare') || lowerMessage.includes('vs')) tags.push('comparison');
    if (lowerMessage.includes('improve') || lowerMessage.includes('training')) tags.push('training');
    if (lowerMessage.includes('predict') || lowerMessage.includes('future')) tags.push('prediction');
    
    return tags;
  };

  const handleVoiceToggle = () => {
    if (isListening) {
      setIsListening(false);
      // Stop voice recognition
    } else {
      setIsListening(true);
      // Start voice recognition
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleFavorite = (messageId: string) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, favorite: !msg.favorite } : msg
    ));
  };

  const exportConversation = () => {
    if (!currentConversation) return;
    
    const data = JSON.stringify(currentConversation, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cricket-conversation-${currentConversation.id}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const clearConversation = () => {
    setMessages([]);
    setCurrentConversation(null);
  };

  return (
    <div className={`flex flex-col h-full max-h-[800px] ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Cricket Analytics Assistant</h2>
          <Badge variant={isConnected ? 'default' : 'destructive'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
          >
            <History className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportConversation}
            disabled={!currentConversation}
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearConversation}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Connection Status */}
      {!isConnected && (
        <Alert className="m-4">
          <AlertDescription>
            Analytics assistant is currently offline. Some features may be limited.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Conversation History Sidebar */}
        {showHistory && (
          <div className="w-80 border-r bg-gray-50/50">
            <div className="p-4">
              <h3 className="font-medium mb-4">Conversation History</h3>
              <ScrollArea className="h-full">
                {conversations.map(conv => (
                  <Card 
                    key={conv.id} 
                    className={`mb-2 cursor-pointer transition-colors ${
                      currentConversation?.id === conv.id ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                    onClick={() => {
                      setCurrentConversation(conv);
                      setMessages(conv.messages);
                    }}
                  >
                    <CardContent className="p-3">
                      <p className="text-sm font-medium truncate">{conv.title}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {conv.updatedAt.toLocaleDateString()}
                      </p>
                      <div className="flex gap-1 mt-2">
                        {conv.tags.map(tag => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </ScrollArea>
            </div>
          </div>
        )}

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            {messages.length === 0 ? (
              <div className="text-center text-gray-500 mt-8">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">Ask me anything about your cricket performance!</h3>
                <p className="text-sm">
                  Try asking: "How is my batting improving?", "What should I focus on in training?", 
                  or "Compare my performance with last week"
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-4 ${
                        message.type === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="whitespace-pre-wrap">{message.content}</p>
                          
                          {/* Query Type Badge */}
                          {message.queryType && (
                            <Badge 
                              variant="outline" 
                              className={`mt-2 ${message.type === 'user' ? 'border-white/50' : ''}`}
                            >
                              {message.queryType}
                            </Badge>
                          )}
                          
                          {/* Confidence Indicator */}
                          {message.confidence !== undefined && (
                            <div className="mt-2">
                              <div className="flex items-center gap-2 text-xs">
                                <span>Confidence:</span>
                                <Progress 
                                  value={message.confidence * 100} 
                                  className="w-20 h-2" 
                                />
                                <span>{Math.round(message.confidence * 100)}%</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Visualizations */}
                          {message.visualizations && message.visualizations.length > 0 && (
                            <div className="mt-3">
                              <ResponseGeneration 
                                visualizations={message.visualizations}
                                queryType={message.queryType}
                              />
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between mt-2 text-xs opacity-70">
                            <span>{message.timestamp.toLocaleTimeString()}</span>
                          </div>
                        </div>
                        
                        {message.type === 'assistant' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleFavorite(message.id)}
                            className={`h-6 w-6 p-0 ${message.favorite ? 'text-yellow-500' : 'text-gray-400'}`}
                          >
                            <Star className="h-3 w-3" fill={message.favorite ? 'currentColor' : 'none'} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg p-4 max-w-[80%]">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                        <span className="text-sm text-gray-600">Analyzing your query...</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div ref={messagesEndRef} />
          </ScrollArea>

          {/* Input Area */}
          <div className="border-t p-4">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about performance, training plans, comparisons, or predictions..."
                  disabled={isLoading}
                  className="min-h-[40px] resize-none"
                />
              </div>
              
              <VoiceInterface 
                isListening={isListening}
                onToggle={handleVoiceToggle}
                onTranscript={setInputValue}
              />
              
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                size="sm"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            
            <QueryUnderstanding 
              query={inputValue}
              className="mt-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
}